import React from 'react';
import { CallRecordKey, QueueKey } from 'flow/types/EntityKeys';
import { twMerge } from 'tailwind-merge';
import { useAppSelector } from 'flow/hooks/useRedux';
import {
    selectCallRecordConnectedTime,
    selectCallRecordOmniTicketEntityKey,
    selectCallRecordPhoneNumber,
} from 'flow/selectors/call-records-selectors';
import ButtonCreateShowTicket from 'flow/components/pbx/v2/misc/ButtonCreateShowTicket/ButtonCreateShowTicket';
import { SegmentContextType } from 'flow/components/call-history-v2/types';
import CallRecordCallButton from 'flow/components/pbx/v2/detail-page/CallRecordCallButton';
import CallRecordContextMenu from './CallRecordContextMenu';

interface Props {
    callRecordKey: CallRecordKey;
    queueKey?: QueueKey;
    segmentContext: SegmentContextType;
    contextMenuIsActive: boolean;
    setContextMenuIsActive: (active: boolean) => void;
}

const CallRecordActions = ({
    callRecordKey,
    queueKey,
    segmentContext,
    contextMenuIsActive,
    setContextMenuIsActive,
}: Props) => {
    const ticketKey = useAppSelector((state) =>
        selectCallRecordOmniTicketEntityKey(state, callRecordKey),
    );
    const callerNumber = useAppSelector((state) =>
        selectCallRecordPhoneNumber(state, callRecordKey),
    );
    const connected = useAppSelector((state) =>
        selectCallRecordConnectedTime(state, callRecordKey),
    );

    if (!connected) return null;

    return (
        <div
            className={twMerge(
                // Positioned, and later in the DOM than the stretched click
                // area of the title's link or button, so the buttons take the
                // click. No z-index: that would lift them above the sticky
                // date header.
                'absolute end-2 top-2 items-center gap-x-2',
                contextMenuIsActive
                    ? 'flex'
                    : 'sr-only-until-hover-or-focus flex',
            )}
        >
            {queueKey && (
                <ButtonCreateShowTicket
                    queueKey={queueKey}
                    callRecordKey={callRecordKey}
                    ticketKey={ticketKey}
                    callerNumber={callerNumber}
                    callTimestamp={connected}
                    buttonType="icon"
                    segmentContext={segmentContext}
                />
            )}
            <CallRecordCallButton
                callRecordKey={callRecordKey}
                size="small"
                activeBg={true}
            />
            <CallRecordContextMenu
                callRecordKey={callRecordKey}
                queueKey={queueKey}
                segmentContext={segmentContext}
                setActive={setContextMenuIsActive}
            />
        </div>
    );
};

export default React.memo(CallRecordActions);
